import React, { useEffect, useState, useRef } from 'react';
import { Card, Badge, Image, Button } from 'react-bootstrap';

import './index.scss';
import { amenitiesConfig } from '../../mockups/DriverDetails';
import { formatDateTimeWithDayAbbr } from '../../../../utils/helper';
import { useAccountState, useBookingState, useVehicleAssignmentsState } from '../../../../Context';

const SectionHeader = ({ title, color }) => (
    <div
        className="px-3 py-2 font-weight-bold text-uppercase portal-text mobile-section-header"
        style={{ backgroundColor: color || '#999' }}
    >
        {title}
    </div>
);

const SalespersonCard = ({ details }) => (
    <div className="d-flex align-items-center p-3 bg-white border-bottom">
        <div className="mobile-salesperson-img-box">
            <Image
                src={details.salesPersonPhoto || 'https://placehold.co/150?text=Salesperson'}
                className="mobile-salesperson-img"
            />
        </div>
        <div className="ml-3 w-100">
            <h4 className="font-weight-bold mb-0 text-dark">{details.salesPersonFullName}</h4>
            <div className="text-muted mb-2" style={{ fontSize: '1rem' }}>
                Salesperson
            </div>
            <div className="d-flex align-items-center">
                <a href={`mailto:${details.salesPersonEmail}`} className="text-dark mr-4">
                    <i className="far fa-envelope fa-2x"></i>
                </a>
                <a href={`tel:${details.salesPersonPhone}`} className="text-dark">
                    <i className="fas fa-phone-alt fa-2x"></i>
                </a>
            </div>
        </div>
    </div>
);

const DriverVehicleCard = ({ vehicle, isSelected, onClick, tripSettings }) => {
    const {
        cp_showDriverFirstName,
        cp_showDriverLastName,
        cp_showDriverPhoneNumber,
        cp_showDriverPhoto,
        buttonColor,
        cp_showVehicleExteriorPhoto,
        cp_showVehicleInteriorPhoto,
        cp_showVehicleType,
        cp_showVehicleId,
    } = tripSettings || {};

    const is_driverAssigned = !!vehicle?.driverID;
    const is_vehicleAssigned = !!vehicle?.vehicleID;

    const hasDriverInfo =
        !!(cp_showDriverFirstName || cp_showDriverLastName || cp_showDriverPhoneNumber || cp_showDriverPhoto) &&
        is_driverAssigned;

    const hasVehicleInfo =
        !!(cp_showVehicleType || cp_showVehicleId || cp_showVehicleExteriorPhoto || cp_showVehicleInteriorPhoto) &&
        is_vehicleAssigned;

    const photoUrl =
        cp_showDriverPhoto && vehicle.driverPhoto ? vehicle.driverPhoto : 'https://placehold.co/150?text=Driver';

    const firstName = cp_showDriverFirstName ? (vehicle?.driverFirstName ?? '') : '';
    const lastName = cp_showDriverLastName ? (vehicle?.driverLastName ?? '') : '';
    const showPhone = cp_showDriverPhoneNumber && vehicle?.mobilePhone;

    const hasExteriorPhoto = cp_showVehicleExteriorPhoto && vehicle.vehicleExteriorPhoto;
    const hasInteriorPhoto = cp_showVehicleInteriorPhoto && vehicle.vehicleInteriorPhoto;
    const fallbackPhoto = 'https://placehold.co/200x120?text=Vehicle';

    const photos = [];
    if (hasExteriorPhoto) photos.push({ url: vehicle.vehicleExteriorPhoto, type: 'Exterior' });
    if (hasInteriorPhoto) photos.push({ url: vehicle.vehicleInteriorPhoto, type: 'Interior' });
    if (photos.length === 0) photos.push({ url: fallbackPhoto, type: 'Vehicle' });

    const carouselId = `vehicle-carousel-${vehicle.id || Math.random()}`;

    return (
        <Card
            className="border-0 shadow-sm mx-3 mb-3 mobile-driver-vehicle-card"
            style={{
                border: isSelected ? `2px solid ${buttonColor || '#007bff'}` : '2px solid transparent',
            }}
            onClick={onClick}
        >
            <Card.Body className="p-3 d-flex flex-column">
                {hasDriverInfo ? (
                    <div className="d-flex align-items-center mb-3 pb-3 border-bottom">
                        <div
                            className="mobile-dv-img-box mr-3"
                            style={{ width: '60px', height: '60px', flexShrink: 0 }}
                        >
                            <Image src={photoUrl} rounded className="img-cover" />
                        </div>
                        <div className="ml-3">
                            <h5 className="font-weight-bold mb-0 text-dark" style={{ fontSize: '1.1rem' }}>
                                {firstName} {lastName}
                            </h5>
                            <small className="text-muted d-block mb-1">Driver</small>
                            <div>
                                {!!showPhone && (
                                    <a
                                        href={`tel:${vehicle.mobilePhone}`}
                                        className="text-dark mr-3"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <i className="fas fa-phone-alt fa-lg"></i>
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="d-flex align-items-center mb-3 pb-3 border-bottom text-muted">
                        <i className={`fas ${is_driverAssigned ? 'fa-user-check' : 'fa-user-clock'} fa-2x mr-3`}></i>
                        <div className="font-weight-bold">
                            {is_driverAssigned ? 'Driver Assigned' : 'No Driver Assigned'}
                        </div>
                    </div>
                )}

                {hasVehicleInfo ? (
                    <div className="d-flex justify-content-between">
                        <div className="pr-2" style={{ flex: 1 }}>
                            {!!cp_showVehicleType && (
                                <h6
                                    className="font-weight-bold mb-1 text-dark"
                                    style={{ fontSize: '1rem', lineHeight: '1.2' }}
                                >
                                    {vehicle.vehicleType}
                                </h6>
                            )}
                            <div className="mb-2">
                                <span className="text-muted mobile-vehicle-pill">
                                    Seats up to {vehicle.seats || vehicle.capacity || 'N/A'}
                                </span>
                            </div>
                            <div className="text-dark" style={{ fontSize: '1rem' }}>
                                {amenitiesConfig.map((item) =>
                                    vehicle[item.key] ? (
                                        <i key={item.key} className={`${item.icon} mr-3`} title={item.title}></i>
                                    ) : null
                                )}
                            </div>
                        </div>
                        <div className="mobile-vehicle-img-container">
                            {photos.length > 1 ? (
                                <div id={carouselId} className="carousel slide h-100" data-ride="carousel">
                                    <div className="carousel-inner h-100">
                                        {photos.map((photo, index) => (
                                            <div
                                                key={index}
                                                className={`carousel-item h-100 ${index === 0 ? 'active' : ''}`}
                                            >
                                                <Image
                                                    src={photo.url}
                                                    className="mobile-vehicle-img d-block w-100"
                                                    alt={photo.type}
                                                />
                                            </div>
                                        ))}
                                    </div>

                                    <a
                                        className="carousel-control-prev"
                                        href={`#${carouselId}`}
                                        role="button"
                                        data-slide="prev"
                                    >
                                        <span className="carousel-control-prev-icon" />
                                    </a>
                                    <a
                                        className="carousel-control-next"
                                        href={`#${carouselId}`}
                                        role="button"
                                        data-slide="next"
                                    >
                                        <span className="carousel-control-next-icon" />
                                    </a>
                                </div>
                            ) : (
                                <Image src={photos[0].url} className="mobile-vehicle-img" alt={photos[0].type} />
                            )}

                            {!!cp_showVehicleId && (
                                <Badge
                                    variant="secondary"
                                    className="position-absolute"
                                    style={{
                                        bottom: '-10px',
                                        right: '0',
                                        fontSize: '0.7rem',
                                        backgroundColor: '#6c757d',
                                        opacity: 0.9,
                                    }}
                                >
                                    ID {vehicle.vehicleID}
                                </Badge>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="d-flex align-items-center pt-1 text-muted">
                        <i className={`fas ${is_vehicleAssigned ? 'fa-bus' : 'fa-clock'} fa-2x mr-3`}></i>
                        <div className="font-weight-bold">
                            {is_vehicleAssigned ? 'Vehicle Assigned' : 'No Vehicle Assigned'}
                        </div>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

const DriverDetails = (props) => {
    const bookingId = props.match.params.id;
    const isAuthenticated = Boolean(localStorage.getItem('token'));
    const {
        bookings: { bookingDetails, vehicles },
    } = useBookingState();
    const {
        tripSettings: { tripSettings },
    } = useAccountState();
    const {
        vehicleAssignments: { vehicleAssignments },
    } = useVehicleAssignmentsState();

    const [selectedRouteId, setSelectedRouteId] = useState(null);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (vehicleAssignments && vehicleAssignments.length > 0 && !selectedRouteId) {
            setSelectedRouteId(vehicleAssignments[0].id);
        }
    }, [vehicleAssignments, selectedRouteId]);

    const handleOpenMaps = (address) => {
        if (address)
            window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank');
    };

    const handleOpenTracking = () => {
        console.log('Open tracking for vehicle:', selectedVehicle);
    };

    const toggleVehicleSelection = (vehicle) => {
        if (selectedVehicle && selectedVehicle.id === vehicle.id) {
            setSelectedVehicle(null);
        } else {
            setSelectedVehicle(vehicle);
        }
    };

    const scrollRoutes = (direction) => {
        if (scrollRef.current) {
            const { current } = scrollRef;
            const scrollAmount = 200;
            if (direction === 'left') {
                current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
            } else {
                current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
            }
        }
    };

    const currentAssignment = vehicleAssignments.find((a) => a.id === selectedRouteId);
    const currentVehicles = vehicles.filter((v) => v.vehicleAssignmentId === selectedRouteId);

    return (
        <div className="w-100 pb-5" style={{ backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
            <div className="d-flex align-items-center justify-content-between p-3 bg-white shadow-sm sticky-top">
                <div
                    className="font-weight-bold text-dark d-flex align-items-center"
                    style={{ fontSize: '1.1rem', cursor: isAuthenticated ? 'pointer' : 'default' }}
                    onClick={isAuthenticated ? () => props.history.push(`/bookings/${bookingId}`) : () => {}}
                >
                    {isAuthenticated && (
                        <i className="fas fa-chevron-left mr-2" style={{ color: tripSettings.buttonColor }} />
                    )}
                    Booking {bookingDetails.bookingId}
                </div>
                {bookingDetails.purchaseOrder && (
                    <Badge variant="success" className="p-2" style={{ backgroundColor: '#4b7c2a', fontSize: '0.9rem' }}>
                        PO# {bookingDetails.purchaseOrder}
                    </Badge>
                )}
            </div>

            <SectionHeader title="TRIP DETAILS" color={tripSettings?.sectionColor} />
            <SalespersonCard details={bookingDetails} />

            {vehicleAssignments.length > 1 && (
                <>
                    <SectionHeader title="ROUTE INFORMATIONS" color={tripSettings?.sectionColor} />
                    <div className="route-information d-flex align-items-center bg-white border-bottom py-2 px-1">
                        <Button variant="link" onClick={() => scrollRoutes('left')} className="p-2">
                            <i className="fas fa-chevron-left text-dark" />
                        </Button>
                        <div className="d-flex overflow-auto mobile-route-scroller" ref={scrollRef}>
                            {vehicleAssignments.map((assignment) => (
                                <Button
                                    variant="outline-primary"
                                    key={assignment.id}
                                    onClick={() => {
                                        setSelectedRouteId(assignment.id);
                                        setSelectedVehicle(null);
                                    }}
                                    className="mx-1 shadow-sm text-truncate mobile-route-btn"
                                    active={selectedRouteId === assignment.id}
                                    title={assignment.routeDescription}
                                    style={{
                                        backgroundColor: selectedRouteId === assignment.id ? '#eb592a' : '#e0e0e0',
                                        color: selectedRouteId === assignment.id ? '#fff' : '#333',
                                    }}
                                >
                                    {assignment.routeDescription || `Route #${assignment.id}`}
                                </Button>
                            ))}
                        </div>
                        <Button variant="link" onClick={() => scrollRoutes('right')} className="p-2">
                            <i className="fas fa-chevron-right text-dark" />
                        </Button>
                    </div>
                </>
            )}

            {currentAssignment && (
                <>
                    <SectionHeader title="DISPATCH INFORMATION" color={tripSettings?.sectionColor} />
                    <div className="bg-white p-3 mb-2 shadow-sm border-bottom">
                        <div className="mb-3">
                            <label className="font-weight-bold d-block text-dark mb-1">Route Description</label>
                            <span className="text-dark">{currentAssignment.routeDescription}</span>
                        </div>
                        <div className="mb-3 border-top pt-2">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <label className="font-weight-bold text-dark mb-0">First Pick-Up Location</label>
                                <small
                                    className="text-dark cursor-pointer"
                                    onClick={() => handleOpenMaps(currentAssignment.firstPickupLocation)}
                                >
                                    Open in Maps
                                </small>
                            </div>
                            <span className="text-dark d-block">{currentAssignment.firstPickupLocation}</span>
                        </div>
                        <div className="border-top pt-2">
                            <label className="font-weight-bold d-block text-dark mb-1">First Pick-Up Time</label>
                            <span className="text-dark">
                                {formatDateTimeWithDayAbbr(currentAssignment.firstDepartureTime)}
                            </span>
                        </div>
                    </div>

                    <SectionHeader title="DRIVERS & VEHICLES" color={tripSettings?.sectionColor} />
                    <div className="mt-1">
                        {/* {currentVehicles.length > 0 && (
                            <div className="d-flex align-items-center px-3 mb-2 text-muted">
                                <i className="fas fa-info-circle mr-2" style={{ color: '#17a2b8' }}></i>
                                <small>Toggle a card below to view tracking details</small>
                            </div>
                        )} */}
                        {currentVehicles.map((vehicle, idx) => {
                            const isSelected = selectedVehicle && selectedVehicle.id === vehicle.id;
                            return (
                                <div key={vehicle.id || idx}>
                                    <DriverVehicleCard
                                        vehicle={vehicle}
                                        isSelected={isSelected}
                                        onClick={() => toggleVehicleSelection(vehicle)}
                                        tripSettings={tripSettings}
                                    />
                                </div>
                            );
                        })}
                        {currentVehicles.length === 0 && (
                            <div className="text-center p-4 text-muted">No vehicles assigned to this route.</div>
                        )}
                    </div>
                </>
            )}

            {/* We should not include any logic or visuals towards Vehicle Tracking at the moment.  */}
            {/* {selectedVehicle && (
                <div
                    className="fixed-bottom text-white p-3 d-flex justify-content-between align-items-center shadow-lg slide-up-animation mobile-sticky-footer"
                    onClick={handleOpenTracking}
                >
                    <div>
                        <span className="font-weight-bold text-uppercase d-block" style={{ fontSize: '0.9rem' }}>
                            TRACKING VEHICLE #{selectedVehicle.vehicleID}
                        </span>
                        <small style={{ color: '#ccc' }}>Tap to open map</small>
                    </div>
                    <i className="fas fa-external-link-alt fa-lg"></i>
                </div>
            )} */}
        </div>
    );
};

export default DriverDetails;
